import { createPathfinder } from "controllers/pathfinder";

const palette = {
  background: "#04111a",
  grid: "#112433",
  wall: "#5b6a74",
  open: "#1f88a7",
  closed: "#123149",
  path: "#ffd166",
  start: "#06d6a0",
  end: "#ef476f",
};

function sketch({ getLayoutOptions, getRuntimeOptions, onStatsChange } = {}) {
  let simulation = null;
  let p5 = null;
  let canvasParent = null;
  let cellWidth = 0;
  let cellHeight = 0;
  let pendingActions = [];
  let lastPublish = 0;
  let lastSignature = "";

  function publishSnapshot(force = false) {
    if (!simulation || !onStatsChange) {
      return;
    }

    const snapshot = simulation.getSnapshot();
    const payload = {
      status: snapshot.status,
      stepCount: snapshot.stepCount,
      exploredCount: snapshot.exploredCount,
      frontierCount: snapshot.frontierCount,
      pathLength: snapshot.pathLength,
      startLabel: `${snapshot.start.x}, ${snapshot.start.y}`,
      endLabel: `${snapshot.end.x}, ${snapshot.end.y}`,
    };
    const signature = JSON.stringify(payload);

    if (
      !force &&
      signature === lastSignature &&
      Date.now() - lastPublish < 120
    ) {
      return;
    }

    lastSignature = signature;
    lastPublish = Date.now();
    onStatsChange(payload);
  }

  function updateCellSize() {
    if (!p5 || !simulation) {
      return;
    }

    const { columns, rows } = simulation.getSnapshot().grid;
    cellWidth = p5.width / columns;
    cellHeight = p5.height / rows;
  }

  function resolveCanvasSize() {
    const parentWidth = canvasParent?.clientWidth ?? window.innerWidth;
    const maxWidth = Math.min(parentWidth, window.innerWidth - 32);
    const maxHeight =
      window.innerWidth < 960
        ? window.innerHeight * 0.5
        : window.innerHeight * 0.76;

    return Math.max(280, Math.floor(Math.min(maxWidth, maxHeight)));
  }

  function resizeCanvas() {
    if (!p5) {
      return;
    }

    const nextSize = resolveCanvasSize();
    p5.resizeCanvas(nextSize, nextSize);
    updateCellSize();
  }

  function regenerate() {
    simulation = createPathfinder(getLayoutOptions());
    updateCellSize();
    publishSnapshot(true);
  }

  function processPendingActions() {
    while (pendingActions.length > 0) {
      const action = pendingActions.shift();

      if (action === "reset") {
        simulation.resetSearch();
        publishSnapshot(true);
      }

      if (action === "regenerate") {
        regenerate();
      }

      if (action === "resize") {
        resizeCanvas();
      }
    }
  }

  function drawCell(item) {
    const x = item.x * cellWidth;
    const y = item.y * cellHeight;

    p5.stroke(palette.grid);
    p5.strokeWeight(1);

    if (item.wall) {
      p5.fill(palette.wall);
    } else if (item.start) {
      p5.fill(palette.start);
    } else if (item.end) {
      p5.fill(palette.end);
    } else if (item.isPath) {
      p5.fill(palette.path);
    } else if (item.isOpen) {
      p5.fill(palette.open);
    } else if (item.isClosed) {
      p5.fill(palette.closed);
    } else {
      p5.noFill();
    }

    p5.rect(x, y, cellWidth, cellHeight);
  }

  function drawGrid() {
    const snapshot = simulation.getSnapshot();

    p5.background(palette.background);

    snapshot.grid.items.forEach((item) => {
      drawCell(item);
    });
  }

  function setup(nextP5, canvasParentRef) {
    p5 = nextP5;
    canvasParent = canvasParentRef;

    while (canvasParentRef.firstChild) {
      canvasParentRef.removeChild(canvasParentRef.firstChild);
    }

    const initialSize = resolveCanvasSize();

    p5.createCanvas(initialSize, initialSize).parent(canvasParentRef);
    regenerate();
  }

  function draw() {
    if (!simulation) {
      return;
    }

    processPendingActions();

    const runtime = getRuntimeOptions();

    if (!runtime.paused) {
      for (
        let stepIndex = 0;
        stepIndex < runtime.stepsPerFrame;
        stepIndex += 1
      ) {
        const snapshot = simulation.step();

        if (snapshot.status !== "searching") {
          break;
        }
      }
    }

    drawGrid();
    publishSnapshot();
  }

  function windowResized() {
    pendingActions.push("resize");
  }

  function dispatch(action) {
    pendingActions.push(action.type);
  }

  return { draw, setup, windowResized, dispatch };
}

export { sketch };
